/**
 * Legacy Jobs → Shifts list status display (mirrors backend shiftListStatusDisplay.js).
 */

import { getAppDisplayTimezone, formatAppTime, parseDbDateTime } from "./appDateTime";

export const SHIFT_CHECKIN_STATE_LABELS = {
  draft: "Draft",
  unassigned: "Unassigned",
  tentative: "Tentative",
  declined: "Unfilled",
  deleted: "Deleted",
  cancelled: "Cancelled",
  future: "Future",
  checkIn: "Checked in",
  late: "Late",
  noshow: "No show",
  signoffPending: "Pending signoff",
  signoffPendingReview: "Pending signoff review",
  signoffComplete: "Signoff complete",
};

export const SHIFT_CHECKIN_STATE_COLORS = {
  draft: "#9e9e9e",
  unassigned: "#A9A9A9",
  tentative: "#f39c59",
  future: "#A37DB0",
  late: "#c49191",
  checkIn: "#aac4a2",
  noshow: "#facd91",
  signoffPending: "#00AAAA",
  signoffPendingReview: "#60A5C2",
  signoffComplete: "#9191A6",
  cancelled: "#D9534F",
  declined: "#C70F0F",
  deleted: "#8C2323",
};

const SHOW_CHECKIN_INFO_EXCLUDED = new Set([
  "draft",
  "unassigned",
  "tentative",
  "declined",
  "cancelled",
  "future",
  "late",
  "noshow",
  "deleted",
]);

const CHECKIN_STATE_ALIASES = {
  checkin: "checkIn",
  checkedin: "checkIn",
  "checked in": "checkIn",
  signoffpending: "signoffPending",
  "pending signoff": "signoffPending",
  signoffpendingreview: "signoffPendingReview",
  "pending signoff review": "signoffPendingReview",
  signoffcomplete: "signoffComplete",
  "signoff complete": "signoffComplete",
  noshow: "noshow",
  "no show": "noshow",
};

export function normalizeCheckInStateToken(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  if (SHIFT_CHECKIN_STATE_LABELS[trimmed]) return trimmed;
  const key = trimmed.replace(/[\s_]+/g, "").toLowerCase();
  return CHECKIN_STATE_ALIASES[key] || trimmed;
}

function normalizeSignoffToken(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  return parseDbDateTime(value);
}

export function formatShiftDateTime(value, includeDate = true) {
  const dt = parseDate(value);
  if (!dt) return "";
  return dt.toLocaleString("en-US", {
    month: includeDate ? "short" : undefined,
    day: includeDate ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: getAppDisplayTimezone(),
  });
}

/** Legacy check-in-state: time + " - ongoing", or full range when checked out. */
export function formatCheckInTimeLine(checkinDate, checkoutDate) {
  if (!checkinDate) return "";
  if (checkoutDate && parseDate(checkoutDate)) {
    return `${formatShiftDateTime(checkinDate)} - ${formatShiftDateTime(checkoutDate, false)}`;
  }
  return `${formatAppTime(checkinDate)} - ongoing`;
}

const ACCOUNT_TYPE_LABELS = {
  fieldstaff: "Field Staff",
  tempstaff: "Temp Staff",
  officestaff: "Office Staff",
  outsourcedstaff: "Outsourced Staff",
  client: "Client",
  contact: "Contact",
  staff: "Staff",
};

export function formatAccountTypeLabel(type) {
  const raw = String(type || "").trim();
  if (!raw) return "";
  const key = raw.replace(/[\s_]+/g, "").toLowerCase();
  return ACCOUNT_TYPE_LABELS[key] || raw;
}

export function normalizeBreakMinutes(unpaidBreakMinutes) {
  if (
    unpaidBreakMinutes === true ||
    unpaidBreakMinutes === 1 ||
    unpaidBreakMinutes === "1"
  ) {
    return 30;
  }
  const parsed = Number(unpaidBreakMinutes || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function formatDurationAfterBreak(
  start,
  end,
  unpaidBreakMinutes,
  options = {},
) {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) return "";
  const breakMinutes = normalizeBreakMinutes(unpaidBreakMinutes);
  const minutes = Math.max(
    Math.round((to.getTime() - from.getTime()) / 60000) - breakMinutes,
    0,
  );
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const duration = `${hours}h ${String(mins).padStart(2, "0")}m`;
  if (breakMinutes > 0) {
    return `${duration} after ${breakMinutes}m break`;
  }
  if (options?.showZeroBreak && breakMinutes === 0) {
    return `${duration} after 0m break`;
  }
  return duration;
}

export function shouldShowCheckinInfo(checkInState) {
  const token = normalizeCheckInStateToken(checkInState);
  return !!token && !SHOW_CHECKIN_INFO_EXCLUDED.has(token);
}

export function forgotToCheckout(row, now = new Date()) {
  const checkinDate = parseDate(row.checkinDateIso || row.checkin_date);
  const checkoutDate = parseDate(row.checkoutDateIso || row.checkout_date);
  const endDate = parseDate(row.endDateIso || row.end_date);
  if (!checkinDate || checkoutDate || !endDate) return false;
  const cutoff = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
  return cutoff < now;
}

function resolveNoCheckinTimestampLines(row) {
  const signoffDate =
    row.checkinSignoffDateIso ||
    row.checkin_signoff_date ||
    row.timesheetSignOffDateIso ||
    row.timesheet_sign_off_date ||
    null;
  const actualStart =
    row.actualStartDateIso ||
    row.actualStartDate ||
    row.actual_start_date ||
    null;
  const actualEnd =
    row.actualEndDateIso ||
    row.actualEndDate ||
    row.actual_end_date ||
    null;
  const schedStart = row.startDateIso || row.start_date || null;
  const schedEnd = row.endDateIso || row.end_date || null;
  const rawBreak =
    row.actualUnpaidBreakMinutes ??
    row.actual_unpaid_break_minutes ??
    row.checkinUnpaidBreakMinutes ??
    row.checkin_unpaid_break_minutes ??
    row.unpaid_break ??
    0;
  const breakMinutes = normalizeBreakMinutes(rawBreak);

  const displayStart = actualStart || schedStart;
  const displayEnd = actualEnd || schedEnd;

  if (displayStart && displayEnd) {
    return {
      line1: `${formatShiftDateTime(displayStart)} - ${formatShiftDateTime(displayEnd, false)}`,
      line2: formatDurationAfterBreak(displayStart, displayEnd, breakMinutes),
    };
  }
  if (signoffDate) {
    return { line1: formatShiftDateTime(signoffDate), line2: null };
  }
  if (displayStart) {
    return { line1: formatShiftDateTime(displayStart), line2: null };
  }
  return { line1: "No Checkin", line2: null };
}

function isSubmittedActualState(raw) {
  const state = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "_");
  return ["as_scheduled", "asscheduled", "different", "no_show", "noshow"].includes(
    state,
  );
}

/** List chip state: SQL checkInState corrected for actual-submit parity. */
export function resolveListCheckInState(row) {
  const checkInState = normalizeCheckInStateToken(
    row.checkInState || row.checkinState || row.check_in_state || "",
  );
  const actualRaw =
    row.actualState ?? row.actual_state ?? row.chosenActualState ?? "";
  const signoff = normalizeSignoffToken(
    row.signoffState ?? row.signoff_state ?? "",
  );
  const checkin = row.checkinDateIso || row.checkin_date || null;
  const submitted = isSubmittedActualState(actualRaw);

  const preSignoffStates = new Set(["future", "late", "tentative"]);

  if (submitted && !checkin) {
    if (signoff === "pendingreview" || signoff === "pendingsignoffreview") {
      return "signoffPendingReview";
    }
    if (preSignoffStates.has(checkInState) || checkInState === "signoffPending") {
      return "signoffComplete";
    }
  }

  if (
    (checkInState === "signoffComplete" ||
      checkInState === "signoffPendingReview") &&
    !submitted
  ) {
    return "signoffPending";
  }
  return checkInState;
}

export function buildShiftListStatusDisplay(row, now = new Date()) {
  const checkInState = resolveListCheckInState(row);

  const chipState = checkInState;
  const chipLabel = SHIFT_CHECKIN_STATE_LABELS[chipState] || chipState || "";
  const chipColor = SHIFT_CHECKIN_STATE_COLORS[chipState] || "#9e9e9e";

  const showCheckinInfo = shouldShowCheckinInfo(chipState);

  let line1 = null;
  let line2 = null;
  let forgotCheckout = false;

  if (showCheckinInfo) {
    const checkinDate = row.checkinDateIso || row.checkin_date;
    const checkoutDate = row.checkoutDateIso || row.checkout_date;
    const breakMinutes =
      row.checkinUnpaidBreakMinutes ?? row.checkin_unpaid_break_minutes ?? 0;

    if (!checkinDate) {
      line1 = "No Checkin";
    } else if (checkinDate && checkoutDate) {
      line1 = `${formatShiftDateTime(checkinDate)} - ${formatShiftDateTime(checkoutDate, false)}`;
      line2 = formatDurationAfterBreak(checkinDate, checkoutDate, breakMinutes);
    } else {
      line1 = formatShiftDateTime(checkinDate);
    }

    forgotCheckout = forgotToCheckout(row, now);
  }

  return {
    chipState,
    chipLabel,
    chipColor,
    showCheckinInfo,
    line1,
    line2,
    forgotToCheckout: forgotCheckout,
  };
}

export function resolveShiftStatusDisplay(shift) {
  return buildShiftListStatusDisplay(shift);
}
